import { CommonModule } from '@angular/common';
import { Component, Input } from "@angular/core";
import { OpcaoMenuSuspenso } from "../menu-suspenso/menu-suspenso.component";
import { DadosRelatorioCaqDto } from "../../dtos/caq-por-etapa-dto";
import { CaqUtils, CategoriaFiltro } from "../../utils/CaqUtils";

export interface CategoriaFiltroExpor extends CategoriaFiltro{
  etapaLabel: string,
  localidadeLabel: string,
  turnoLabel: string,
  barraClass: string,
}

@Component({
  selector: 'relatorio-caq-por-mes-ano-por-etapa',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './relatorio-caq-por-mes-ano-por-etapa.html',
  styleUrl: 'relatorio-caq-por-mes-ano-por-etapa.scss'
})
export class RelatorioCaqPorAnoPorEtapaComponent {

  @Input() descricaoFiltroFinanciamentoSelecionado = "";

  @Input() tipoFiltroFinanciamentoSelecionado= '0';

  @Input() dadosRelatorioCaq: DadosRelatorioCaqDto | null = null;

  @Input() filtroLocalizacaoEscolhido: OpcaoMenuSuspenso = {
    rotulo : "Sem filtros",
    acao: 'NENHUM',
    icone: ""
  }

  private etapasTabela = [
    { etapa: 1, label: 'Creche' },
    { etapa: 2, label: 'Pré-escola' },
    { etapa: 3, label: 'EF-Anos Iniciais' },
    { etapa: 4, label: 'EF-Anos finais' },
    { etapa: 5, label: 'Ensino médio' },
    { etapa: 6, label: 'EJA' },
  ];

  public turnosTabela = [
    { label: 'Parcial', ehParcial: true, barraClass: 'div-caq-projecao-valor' },
    { label: 'Integral', ehParcial: false, barraClass: 'div-caq-projecao-valor-integral' },
  ];

  // chave da propriedade por largura
  larguraJaCalculadaCache: Map<string,number> = new Map()

    /**
   * Obtém o número de matrículas para a etapa considerando o filtro de localidade.
   *
   * @param etapa 1=Creche, 2=Pré-escola, 3=EF Anos Iniciais, 4=EF Anos Finais, 5=Ensino Médio, 6=EJA
   * @param ehParcial true=Parcial, false=Integral
   * @param ehRural usado apenas quando tipoLocalizacaoFiltro === 'NENHUM' para escolher entre Urbana ou Rural
   */
  obterNumeroMatriculasCategorizada(
    etapa: number,
    ehParcial: boolean,
    ehRural = false
  ): string {
    return CaqUtils.obterNumeroMatriculaCategorizado(
      this.dadosRelatorioCaq,
      {
        etapa: etapa,
        localidade: {
          filtro: this.filtroLocalizacaoEscolhido.acao,
          casoFiltroNenhum: ehRural ? 'RURAL' : 'URBANO_SEM_FILTRO'
        },
        ehParcial: ehParcial
      }
    ).toLocaleString('pt-BR')
  }

  obterNumeroEscolasCategorizada(etapa: number, ehParcial: boolean, ehRural: boolean): string {
    return CaqUtils.obterNumeroEscolasCategorizada(this.dadosRelatorioCaq,
        {
          etapa: etapa,
          localidade: {
            filtro: this.filtroLocalizacaoEscolhido.acao,
            casoFiltroNenhum: ehRural ? 'RURAL' : 'URBANO_SEM_FILTRO'
          },
          ehParcial: ehParcial
        }
      ).toLocaleString('pt-BR')
  }

  /**
   * Obtém o valor do CAQ a partir dos dados recebidos do serviço
   * @param etapa Código da etapa (1: Creche, 2: Pré-escola, etc.)
   * @param ehParcial Se true, busca para turno parcial, se false, para integral
   * @param ehRural Se true, busca localidade rural no caso de filtro de localizacao === 'NENHUM'
   */
  obterValorCAQCategorizada(etapa: number, ehParcial: boolean, ehRural: boolean): string {
    return CaqUtils.obterValorCAQCategorizada(
    this.dadosRelatorioCaq,
    {
      etapa: etapa,
      localidade: {
        filtro: this.filtroLocalizacaoEscolhido.acao,
        casoFiltroNenhum: ehRural ? 'RURAL' : 'URBANO_SEM_FILTRO'
      },
      ehParcial: ehParcial
    },
    this.tipoFiltroFinanciamentoSelecionado === '1'
    ).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  calcularLarguraBarraCAQCategorizada(etapa: number, ehParcial: boolean, ehRural: boolean): number {

    if(!this.dadosRelatorioCaq){
      return 0
    }

    const filtro: CategoriaFiltro = {
        etapa: etapa,
        localidade: {
          filtro: this.filtroLocalizacaoEscolhido.acao,
          casoFiltroNenhum: ehRural ? 'RURAL' : 'URBANO_SEM_FILTRO'
        },
        ehParcial: ehParcial
    }

    const chave = CaqUtils.obterNomePropriedadeBaseCaq(filtro)

    const valorCache = this.larguraJaCalculadaCache.get(chave)

    if( valorCache!==null && valorCache!==undefined  ){
      return valorCache
    }

    this.larguraJaCalculadaCache.set(chave,
      CaqUtils.calcularLarguraBarraCAQCategorizada(
        this.dadosRelatorioCaq,
        filtro,
        this.tipoFiltroFinanciamentoSelecionado === '1'
      )
    )

    return this.larguraJaCalculadaCache.get(chave) ?? 0 ;
  }


  public get filtrosLinhasTabela(): CategoriaFiltroExpor[] {
    const linhas: CategoriaFiltroExpor[] = [];

    const mostrarRural = this.filtroLocalizacaoEscolhido.acao === 'NENHUM';

    const localidadePrincipalLabel =
      this.filtroLocalizacaoEscolhido.acao === 'NENHUM'
        ? 'Urbano'
        : this.filtroLocalizacaoEscolhido.rotulo;

    const blocoRuralOpcional = mostrarRural ?
      [{ label: 'Rural', casoFiltroNenhum: 'RURAL' }] :
      [];

    const blocosLocalidade = [
      { label: localidadePrincipalLabel, casoFiltroNenhum: 'URBANO' },
      ...(blocoRuralOpcional),
    ];

    for (const etapa of this.etapasTabela) {
      for (const bloco of blocosLocalidade) {
        for (const turno of this.turnosTabela) {
          if(etapa.etapa === 6 && !turno.ehParcial) {
            continue
          }
          linhas.push({
            etapa: etapa.etapa,
            ehParcial: turno.ehParcial,
            localidade: {
              filtro: this.filtroLocalizacaoEscolhido.acao,
              casoFiltroNenhum: bloco.casoFiltroNenhum as any,
            },
            etapaLabel: etapa.label,
            localidadeLabel: bloco.label,
            turnoLabel: turno.label,
            barraClass: turno.barraClass,
          });
        }
      }
    }

    return linhas;
  }

  public rowspanEtapaTabela(linha: CategoriaFiltroExpor): number {
    if(linha.etapa===6){ //EJA só tem 1 dos turnos, entt ocupa metade das linhas
      return this.filtroLocalizacaoEscolhido.acao === 'NENHUM' ? 2 : 1
    }
    const mostrarRural = this.filtroLocalizacaoEscolhido.acao === 'NENHUM';
    return this.turnosTabela.length * (mostrarRural ? 2 : 1);
  }

  public showEtapaCell(i: number,linha: CategoriaFiltroExpor): boolean {
    return i % this.rowspanEtapaTabela(linha) === 0;
  }

  public showLocalidadeCell(i: number,linha:CategoriaFiltroExpor): boolean {
    if(linha.etapa===6){ //sempre mostra localidade em EJA
      return true
    }
    return i % this.turnosTabela.length === 0;
  }

  public trackByLinhaTabela (_: number, linha: any): string {
    return `${linha.etapa}-${linha.localidade?.filtro}-${linha.localidade?.casoFiltroNenhum}-${linha.ehParcial}`;
  }

  public isRuralLinha(linha: any): boolean {
    return this.filtroLocalizacaoEscolhido.acao === 'NENHUM'
      && linha?.localidade?.casoFiltroNenhum === 'RURAL';
  }

  //todas as linhas tem border bottom com exceção da ultima linha do EJA
  public borderBottomDaLinha(linha: CategoriaFiltroExpor): string[]{

    if(linha.etapa===6 && linha.localidade.casoFiltroNenhum==='RURAL'  ){
      return []
    }

    if(linha.etapa===6 && linha.localidade.filtro!=='NENHUM'){
      return [ 'mt-1']
    }


    return ['border-bottom']
  }

}
